import type { User } from "firebase/auth";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import type { AssetDynamicField, DepreciationMethod, DepreciationRule } from "../types/imobi";
import { db } from "./firebase";

export type SaveDepreciationRuleInput = {
  id?: string;
  assetTypeName: string;
  usefulLifeYears: number | null;
  annualRate: number;
  residualValuePercent: number;
  method: DepreciationMethod;
  legalReference?: string;
  notes?: string;
  active: boolean;
  dynamicFields?: AssetDynamicField[];
};

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function normalizeRuleId(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("pt-BR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function mapDepreciationRule(id: string, data: Record<string, unknown>): DepreciationRule {
  const dynamicFields = Array.isArray(data.dynamicFields)
    ? data.dynamicFields.map((field) => field as AssetDynamicField).filter((field) => field.id && field.label)
    : [];

  return {
    id,
    assetTypeName: String(data.assetTypeName ?? data.name ?? id),
    usefulLifeYears: typeof data.usefulLifeYears === "number" ? data.usefulLifeYears : null,
    annualRate: typeof data.annualRate === "number" ? data.annualRate : 0,
    residualValuePercent: typeof data.residualValuePercent === "number" ? data.residualValuePercent : 0,
    method: (data.method ?? data.depreciationMethod ?? "linear_mensal") as DepreciationMethod,
    legalReference: data.legalReference ? String(data.legalReference) : undefined,
    notes: data.notes ? String(data.notes) : undefined,
    active: typeof data.active === "boolean" ? data.active : true,
    dynamicFields,
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

export function subscribeDepreciationRules(
  onChange: (rules: DepreciationRule[]) => void,
  onError: (message: string) => void
): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "depreciationRules"), orderBy("assetTypeName")),
    (snapshot) => onChange(snapshot.docs.map((ruleDoc) => mapDepreciationRule(ruleDoc.id, ruleDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function saveDepreciationRule(input: SaveDepreciationRuleInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const assetTypeName = input.assetTypeName.trim();
  const id = input.id || normalizeRuleId(assetTypeName);
  if (!id || !assetTypeName) {
    throw new Error("Informe o nome do tipo de bem.");
  }

  await setDoc(doc(db, "depreciationRules", id), {
    assetTypeName,
    usefulLifeYears: input.usefulLifeYears,
    annualRate: input.annualRate,
    residualValuePercent: input.residualValuePercent,
    method: input.method,
    depreciationMethod: input.method,
    legalReference: input.legalReference ?? "",
    notes: input.notes ?? "",
    active: input.active,
    dynamicFields: input.dynamicFields ?? [],
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    createdAt: serverTimestamp(),
    createdBy: user.uid
  }, { merge: true });
}
